import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  computed,
} from '@angular/core';

import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ValidationMessageComponent } from '../validation-message/validation-message.component';
import { FormBuilderService } from '../../services/form-builder/form-builder.service';
import { Subscription } from 'rxjs';
import { TiptapRichEditorComponent } from '../tiptap-rich-editor/tiptap-rich-editor.component';

@Component({
  selector: 'lib-tiptap-editor-dual-lang',
  imports: [
    ReactiveFormsModule,
    ValidationMessageComponent,
    TranslocoPipe,
    TiptapRichEditorComponent,
  ],
  templateUrl: './tiptap-editor-dual-lang.component.html',
  styleUrl: './tiptap-editor-dual-lang.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TiptapEditorDualLangComponent implements OnInit, OnDestroy {
  private readonly formBuilderService = inject(FormBuilderService);
  private readonly transloco = inject(TranslocoService);

  inputFormControlName = input.required<string>();
  id = input.required<string>();
  name = input.required<string>();

  label = input<string>();
  placeholderEn = input<string>('');
  placeholderAr = input<string>('');
  inputClass = input<string>('');
  width = input<string>('42.3125');
  height = input<string>('6.5');
  isHasToolTip = input<boolean>(false);
  canResize = input<boolean>(true);

  startIcon = input<string>();
  endIcon = input<string>();

  minlength = input<number>();
  maxlength = input<number>(6000);

  disabled = input<boolean>(false);
  readonly = input<boolean>();
  value = input<string | number>();
  requiredEn = input<boolean>(false);
  requiredAr = input<boolean>(false);
  requiredGroup = input<boolean>(false);
  isSingleInput = input<boolean>(false);

  endIconCLick = output<void>();
  typing = output<string>();

  displayPlaceholderAr = computed(() => {
    const providedPlaceholder = this.placeholderAr();
    if (providedPlaceholder) {
      return providedPlaceholder;
    }
    return this.transloco.translate(
      'dual_lang_textarea_default_ar_placeholder'
    );
  });

  currentLang = signal<'EN' | 'AR'>('EN');
  requiredMessageEn = signal<string>('');
  requiredMessageAr = signal<string>('');
  equation = input<string | undefined>(undefined);
  subscriptions = new Subscription();
  html = '';

  private readonly parentContainer = inject(ControlContainer);
  private readonly translocoService = inject(TranslocoService);

  get parentFormGroup() {
    return this.parentContainer.control as FormGroup;
  }

  get childControlEn() {
    const controlName = this.isSingleInput()
      ? this.inputFormControlName()
      : `en`;
    return this.parentFormGroup?.controls[controlName] as FormControl;
  }

  get childControlAr() {
    return this.parentFormGroup?.controls[`ar`] as FormControl;
  }

  ngOnInit(): void {
    if (this.disabled()) {
      this.childControlEn?.disable();
      this.childControlAr?.disable();
    }

    if (this.requiredEn() && this.childControlEn) {
      this.childControlEn.addValidators(Validators.required);
      this.childControlEn.updateValueAndValidity();
      this.handleRequiredValidationEn();
    }

    if (this.requiredAr() && this.childControlAr) {
      this.childControlAr.addValidators(Validators.required);
      this.childControlAr.updateValueAndValidity();
      this.handleRequiredValidationAr();
    }

    this.subscriptions.add(
      this.translocoService.langChanges$.subscribe(lang => {
        if (lang === 'en') {
          this.currentLang.set('EN');
        } else {
          this.currentLang.set('AR');
        }
      })
    );

    this.updateLanguageBasedOnDirection();

    this.handleEquation();
  }

  updateLanguageBasedOnDirection() {
    const isRTL = document.documentElement.dir === 'rtl';

    if (isRTL) {
      this.currentLang.set('AR');
      this.requiredMessageEn.set('اللغة الإنجليزية مطلوبة');
      this.requiredMessageAr.set('اللغة العربية مطلوبة');
    } else {
      this.requiredMessageEn.set('English language is required.');
      this.requiredMessageAr.set('Arabic language is required.');
      this.currentLang.set('EN');
    }
  }

  applyActionOnEndIconClicked() {
    this.endIconCLick.emit();
  }

  switchLanguage(lang: 'EN' | 'AR') {
    this.currentLang.set(lang);
  }

  handleEquation() {
    if (this.equation()) {
      const grandParentFormGroup = this.parentFormGroup.parent as FormGroup;

      const dynamicEquationParams = [
        this.equation() as string,
        this.inputFormControlName(),
        grandParentFormGroup,
      ];

      // handle single equation ONLY
      if (!this.equation()?.includes(' ')) {
        const equationControls =
          this.formBuilderService.addDualLangEquationFormControls(
            dynamicEquationParams
          );
        const equationSubEn = equationControls.en.valueChanges.subscribe(
          value => {
            this.childControlEn?.setValue(value);
          }
        );
        const equationSubAr = equationControls.ar.valueChanges.subscribe(
          value => {
            this.childControlAr?.setValue(value);
          }
        );

        this.subscriptions.add(equationSubEn);
        this.subscriptions.add(equationSubAr);
      }
    }
  }

  handleRequiredValidationEn() {
    this.subscriptions.add(
      this.childControlEn?.valueChanges?.subscribe(value => {
        if (!value) {
          this.childControlEn?.setErrors({ required: true });
        }
      })
    );
  }

  handleRequiredValidationAr() {
    this.subscriptions.add(
      this.childControlAr?.valueChanges?.subscribe(value => {
        if (!value) {
          this.childControlAr?.setErrors({ required: true });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
